import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { User, Target, MessageCircle, TrendingUp, Calendar, Lightbulb, FileText, Shield, ArrowLeft } from 'lucide-react';
import CustomDropdown from './CustomDropdown';
import './CustomDropdown.css';
import { track } from '../services/analyticsEvents';

const Questionnaire = ({ onComplete, profileUrl, onBack, prefill = null }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startedTrackedRef = useRef(false);

  useEffect(() => {
    if (!startedTrackedRef.current) {
      startedTrackedRef.current = true;
      track.questionnaireStarted();
    }
  }, []);
  const [showExitModal, setShowExitModal] = useState(false);
  const [selectCardMinHeight, setSelectCardMinHeight] = useState(null); // measured from Q1 (role)
  const [selectCardWidth, setSelectCardWidth] = useState(null); // measured from Q1 (role)

  // Global Enter key guard to prevent unintended navigation
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      const tag = e.target.tagName?.toLowerCase();
      const isInputLike = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
      const isButton = tag === 'button';
      if (isInputLike || isButton) return; // allow Enter for inputs and explicit buttons only
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, []);

  const questions = [
    {
      id: 'role',
      type: 'select',
      question: "What's your current role or title?",
      icon: <User className="w-5 h-5" />,
      options: [
        'Founder', 'Co-Founder', 'CXO', 'Startup Advisor', 'Angel Investor', 
        'Consultant', 'Strategist', 'Manager', 'Vice President', 'Custom'
      ],
      conditional: {
        'CXO': {
          field: 'cxoType',
          question: 'What type of CXO?',
          options: ['Executive', 'Operating', 'Financial', 'Technology', 'Marketing', 'Information', 'Human Resources', 'Strategy', 'Product', 'Data', 'Digital']
        },
        'Custom': {
          field: 'customRole',
          question: 'Please specify your role:',
          type: 'text'
        }
      }
    },
    {
      id: 'industry',
      type: 'select',
      question: "Which industry or domain best describes your work?",
      icon: <Target className="w-5 h-5" />,
      options: [
        'Technology', 'Finance & Investment', 'Healthcare & Life Sciences', 'Consumer Goods & Retail',
        'E-commerce', 'Marketing & Advertising', 'Real Estate', 'Manufacturing & Industrial',
        'Education & EdTech', 'Media & Entertainment', 'Logistics & Supply Chain', 'Automotive',
        'Consulting & Professional Services', 'Hospitality & Travel', 'Legal & Compliance',
        'Energy & Utilities', 'Government & Public Sector', 'Non-Profit & Social Impact',
        'HR & Recruitment', 'Design & Creative Services', 'Custom'
      ],
      conditional: {
        'Custom': {
          field: 'customIndustry',
          question: 'Please specify your industry:',
          type: 'text'
        }
      }
    },
    {
      id: 'audience',
      type: 'select',
      question: "Who is your primary audience on LinkedIn?",
      icon: <MessageCircle className="w-5 h-5" />,
      options: [
        'Investors & VCs', 'Potential Clients or Customers', 'Future Hires / Talent',
        'Industry Peers & Network', 'Media & PR', 'Board Members / Advisors',
        'Other Founders / Entrepreneurs', 'Executive Recruiters / Headhunters',
        'Partners & Collaborators', 'Students / Mentees / Followers',
        'Analysts & Influencers in My Industry', 'Existing Team & Internal Company Audience', 'Custom'
      ],
      conditional: {
        'Custom': {
          field: 'customAudience',
          question: 'Please specify your audience:',
          type: 'text'
        }
      }
    },
    {
      id: 'goals',
      type: 'checkbox',
      question: "What's holding your current LinkedIn profile back?",
      icon: <TrendingUp className="w-5 h-5" />,
      options: [
        "It doesn't reflect who I am today",
        "The tone feels too generic or robotic",
        "It lacks strategic positioning",
        "My achievements aren't clearly highlighted",
        "It's outdated or incomplete",
        "I don't have time to update it",
        "I'm not sure what to write or showcase",
        "It's not attracting the right people",
        "I haven't prioritised it until now",
        "Other (please specify)"
      ],
      conditional: {
        'Other (please specify)': {
          field: 'customGoals',
          question: 'Please specify:',
          type: 'text'
        }
      }
    },
    {
      id: 'tone',
      type: 'checkbox',
      question: "What matters most to you when it comes to your profile?",
      icon: <Lightbulb className="w-5 h-5" />,
      options: [
        "Positioning myself as a thought leader",
        "Attracting high-quality opportunities (clients, investors, roles)",
        "Building trust and credibility with my audience",
        "Communicating my impact clearly and concisely",
        "Standing out without sounding overdone or salesy",
        "Making it feel personal but professional",
        "Saving time while still looking polished",
        "Other (please specify)"
      ],
      conditional: {
        'Other (please specify)': {
          field: 'customTone',
          question: 'Please specify:',
          type: 'text'
        }
      }
    },
    {
      id: 'preferredTone',
      type: 'checkbox',
      question: "What's your preferred tone? (Select up to 3)",
      icon: <Shield className="w-5 h-5" />,
      maxSelections: 3,
      options: [
        'Professional', 'Credible', 'Strategic', 'Vision-Driven', 
        'Impactful', 'Creative', 'Other (please specify)'
      ],
      conditional: {
        'Other (please specify)': {
          field: 'customPreferredTone',
          question: 'Please specify:',
          type: 'text'
        }
      }
    },
    {
      id: 'postingFrequency',
      type: 'select',
      question: "How frequently do you post on LinkedIn?",
      icon: <Calendar className="w-5 h-5" />,
      options: [
        'Daily', 'Multiple times per week (3-6 times)', 'Weekly (1-2 times)',
        'Bi-weekly (every 2 weeks)', 'Monthly', 'Quarterly (every 3 months)',
        'Rarely (few times per year)', 'Never - I don\'t post content', 'I\'m just getting started'
      ]
    },
    {
      id: 'knownForTopics',
      type: 'checkbox',
      question: "What are the topics you want to be known for? (Select up to 3)",
      icon: <Lightbulb className="w-5 h-5" />,
      maxSelections: 3,
      options: [
        'Leadership', 'Business Strategy', 'Growth & Scaling', 'Marketing & Branding',
        'Product & Innovation', 'Sales & Revenue', 'Technology', 'Finance & Investment',
        'Company Culture', 'Hiring & Talent', 'Customer Experience', 'Fundraising & Startups',
        'Data & Decision-Making', 'Future of Work', 'Personal Branding'
      ]
    },
    {
      id: 'biggestImpact',
      type: 'textarea',
      question: "What's the biggest result or impact you've driven recently?",
      icon: <TrendingUp className="w-5 h-5" />,
      placeholder: "e.g., Led a $50M funding round, scaled team from 10 to 100 employees, launched product that captured 25% market share..."
    },
    {
      id: 'avoidOnProfile',
      type: 'textarea',
      question: "Anything you'd like to avoid or downplay on your profile?",
      icon: <FileText className="w-5 h-5" />,
      placeholder: "e.g., Previous roles, certain industries, specific controversies, etc."
    }
  ];

  const handleAnswerChange = (questionId, value, conditionalField = null) => {
    setAnswers(prev => ({
      ...prev,
      [conditionalField || questionId]: value
    }));
  };

  const getConditionalFor = (question, answer) => {
    if (!question.conditional) return null;
    if (question.type === 'checkbox' && Array.isArray(answer)) {
      const trigger = Object.keys(question.conditional).find(opt => answer.includes(opt));
      return trigger ? { trigger, config: question.conditional[trigger] } : null;
    }
    if (typeof answer === 'string' && answer in question.conditional) {
      return { trigger: answer, config: question.conditional[answer] };
    }
    return null;
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    track.questionnaireCompleted(Object.keys(answers || {}).length);
    setTimeout(() => {
      onComplete(answers);
    }, 1000);
  };

  // Back modal handlers (only relevant on first question)
  const openBackModal = () => setShowExitModal(true);
  const cancelBack = () => setShowExitModal(false);
  const confirmBack = () => {
    setShowExitModal(false);
    onBack && onBack();
  };

  // Close modal on Escape
  useEffect(() => {
    if (!showExitModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowExitModal(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showExitModal]);

  const isQuestionAnswered = (q, ans) => {
    const conditional = getConditionalFor(q, ans);

    if (q.type === 'checkbox') {
      const baseOk = Array.isArray(ans) && ans.length > 0;
      if (!conditional) return baseOk;
      const condVal = answers[conditional.config.field];
      const condOk = conditional.config.type === 'text' ? (condVal && String(condVal).trim() !== '') : !!condVal;
      return baseOk && condOk;
    }

    const baseOk = typeof ans === 'string' && ans.trim() !== '';
    if (!conditional) return baseOk;
    const condVal = answers[conditional.config.field];
    const condOk = conditional.config.type === 'text' ? (condVal && String(condVal).trim() !== '') : !!condVal;
    return baseOk && condOk;
  };

  const isCurrentQuestionAnswered = () => {
    const question = questions[currentQuestion];
    const answer = answers[question.id];
    return isQuestionAnswered(question, answer);
  };

  const firstUnansweredIndex = useMemo(() => {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const ans = (answers && answers[q.id]) || (q.type === 'checkbox' ? [] : '');
      if (!isQuestionAnswered(q, ans)) return i;
    }
    return 0;
  }, [answers]);

  useEffect(() => {
    if (prefill && typeof prefill === 'object') {
      const coerced = { ...prefill };
      // Ensure expected types for each question AND reconcile stored
      // values against the current options list. When an option has
      // been removed or renamed between versions (e.g., an old "Student"
      // role that no longer appears in the dropdown), silently dropping
      // the value would show an empty field and lose the user's answer.
      // Instead we route the old value into the `Custom` conditional
      // slot so the user sees their prior answer and can adjust it.
      for (const q of questions) {
        const v = coerced[q.id];
        if (q.type === 'checkbox') {
          // Normalise to array
          let arr;
          if (typeof v === 'string') {
            try {
              const parsed = JSON.parse(v);
              arr = Array.isArray(parsed) ? parsed : (v ? [v] : []);
            } catch (_) {
              arr = v ? [v] : [];
            }
          } else if (Array.isArray(v)) {
            arr = v.slice();
          } else {
            arr = v != null ? [String(v)] : [];
          }

          // Split into in-options vs orphans (values not in current options)
          const opts = Array.isArray(q.options) ? q.options : [];
          const keep = [];
          const orphans = [];
          for (const item of arr) {
            const s = String(item);
            if (opts.length === 0 || opts.includes(s)) keep.push(s);
            else orphans.push(s);
          }

          // If the question has an "Other (please specify)" conditional
          // and we have orphans, route them there so the user sees them.
          const otherKey = Object.keys(q.conditional || {}).find(
            (k) => /other/i.test(k) && q.conditional[k]?.field
          );
          if (orphans.length > 0 && otherKey) {
            if (!keep.includes(otherKey)) keep.push(otherKey);
            const otherField = q.conditional[otherKey].field;
            const existing = coerced[otherField] ? String(coerced[otherField]) : '';
            const merged = [existing, orphans.join(', ')].filter(Boolean).join(', ');
            coerced[otherField] = merged;
          } else if (orphans.length > 0) {
            console.warn('[Questionnaire] Dropping values not in current options for', q.id, orphans);
          }
          coerced[q.id] = keep;
        } else {
          // select / text / textarea — coerce to string
          let s;
          if (Array.isArray(v)) s = v.join(', ');
          else if (v == null) s = '';
          else s = String(v);

          // For `select` type, if the stored value isn't in the options
          // list but the question exposes a `Custom` conditional with a
          // text field, pivot to Custom mode and keep the stored value
          // visible in the text field.
          if (s && q.type === 'select' && Array.isArray(q.options) && !q.options.includes(s)) {
            const customKey = Object.keys(q.conditional || {}).find(
              (k) => /^custom$/i.test(k) && q.conditional[k]?.field
            );
            if (customKey) {
              const customField = q.conditional[customKey].field;
              if (!coerced[customField]) coerced[customField] = s;
              s = customKey;
              console.info('[Questionnaire] Routing legacy select value to Custom for', q.id, ':', coerced[customField]);
            } else {
              console.warn('[Questionnaire] Stored value not in options for', q.id, ':', s);
            }
          }
          coerced[q.id] = s;
        }
      }
      setAnswers(coerced);
    }
  }, [prefill]);

  useEffect(() => {
    // Always start from question 1, even if prefill exists
    // Users want to see all questions in order, not jump to unanswered ones
    if (prefill && typeof prefill === 'object') {
      setCurrentQuestion(0); // Start from first question
    }
  }, [prefill]);

  const renderQuestion = (question) => {
    const answer = answers[question.id] || (question.type === 'checkbox' ? [] : '');
    const conditionalInfo = getConditionalFor(question, answer);
    const conditional = conditionalInfo?.config || null;
    const conditionalAnswer = conditional ? (answers[conditional.field] || '') : '';

    return (
      <div className="question-container-responsive" style={{ marginTop: 0 }}>
        {/* Question Header - Compact and elegant */}
        <div className="question-header-responsive" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'clamp(8px, 2vw, 12px)', 
          marginBottom: 'clamp(16px, 3vw, 20px)'
        }}>
          <div className="question-icon-responsive" style={{ 
            width: 'clamp(36px, 8vw, 40px)', 
            height: 'clamp(36px, 8vw, 40px)', 
            borderRadius: '10px', 
            background: 'linear-gradient(135deg, #0077B5 0%, #005885 100%)', 
            color: 'white', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 119, 181, 0.2)',
            flexShrink: 0
          }}>
            {question.icon}
          </div>
          <h3 className="question-title-responsive" style={{ 
            margin: 0, 
            fontSize: 'clamp(1rem, 2.5vw, 1.15rem)', 
            color: '#0f172a', 
            fontWeight: 600,
            lineHeight: 1.3
          }}>
            {question.question}
          </h3>
        </div>

        {/* Answer Section - Unified styling */}
        <div style={{ marginTop: 20 }}>
          {question.type === 'select' && (
            <div style={{ marginTop: 8 }}>
              <CustomDropdown
                options={question.options}
                value={answer}
                onChange={(newVal) => {
                  setAnswers(prev => {
                    const prevAns = prev[question.id] || '';
                    const prevCond = getConditionalFor(question, prevAns);
                    const nextCond = getConditionalFor(question, newVal);
                    const next = { ...prev, [question.id]: newVal };
                    if (prevCond && (!nextCond || nextCond.config.field !== prevCond.config.field)) {
                      delete next[prevCond.config.field];
                    }
                    return next;
                  });
                }}
                placeholder="Select an option..."
              />
            </div>
          )}

          {question.type === 'checkbox' && (
            <div style={{ display: 'grid', gap: 'clamp(6px, 1.5vw, 8px)', maxHeight: '45vh', overflowY: 'auto', paddingRight: '4px' }}>
              {question.options.map((option, index) => {
                const isSelected = answer.includes(option);
                return (
                  <label 
                    key={index} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 'clamp(8px, 2vw, 10px)', 
                      padding: 'clamp(10px, 2vw, 14px)', 
                      border: `2px solid ${isSelected ? '#0077B5' : '#e2e8f0'}`, 
                      borderRadius: '10px', 
                      background: isSelected ? '#f0f9ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      boxShadow: isSelected ? '0 2px 6px rgba(0, 119, 181, 0.12)' : 'none',
                      fontSize: 'clamp(0.9rem, 2vw, 0.95rem)'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.08)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        let newAnswer = e.target.checked ? [...answer, option] : answer.filter(item => item !== option);
                        if (question.maxSelections && newAnswer.length > question.maxSelections) return;
                        const condForOption = question.conditional && question.conditional[option];
                        setAnswers(prev => {
                          const next = { ...prev, [question.id]: newAnswer };
                          if (!e.target.checked && condForOption) {
                            delete next[condForOption.field];
                          }
                          return next;
                        });
                      }}
                      style={{ 
                        accentColor: '#0077B5',
                        width: 16,
                        height: 16,
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    />
                    <span style={{ 
                      fontSize: '0.9rem', 
                      color: isSelected ? '#0f172a' : '#475569',
                      fontWeight: isSelected ? 500 : 400,
                      flex: 1,
                      lineHeight: 1.4
                    }}>
                      {option}
                    </span>
                  </label>
                );
              })}
              {question.maxSelections && (
                <p style={{ 
                  fontSize: '0.8rem', 
                  color: '#94a3b8', 
                  marginTop: 6,
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  Select up to {question.maxSelections}
                </p>
              )}
            </div>
          )}

          {question.type === 'textarea' && (
            <div>
              <textarea
                placeholder={question.placeholder}
                value={answer}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                rows={4}
                style={{
                  width: '100%',
                  padding: 'clamp(10px, 2vw, 14px)',
                  fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                  color: '#0f172a',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: 1.5,
                  transition: 'all 0.2s ease',
                  boxShadow: 'none',
                  outline: 'none',
                  minHeight: 'clamp(80px, 15vw, 100px)',
                  maxHeight: '200px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0077B5';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 119, 181, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                }}
              />
            </div>
          )}
        </div>

        {/* Conditional Field - Unified styling */}
        {conditional && (
          <div style={{ 
            marginTop: 'clamp(12px, 2vw, 16px)', 
            padding: 'clamp(12px, 2vw, 14px)', 
            background: '#f8fafc', 
            borderRadius: '10px', 
            border: '1px solid #e2e8f0'
          }}>
            <label style={{ 
              display: 'block', 
              fontSize: 'clamp(0.8rem, 2vw, 0.85rem)', 
              fontWeight: 600, 
              color: '#475569',
              marginBottom: 'clamp(6px, 1vw, 8px)'
            }}>
              {conditional.question}
            </label>
            {conditional.type === 'text' ? (
              <input
                type="text"
                value={conditionalAnswer}
                onChange={(e) => setAnswers(prev => ({ ...prev, [question.id]: answer, [conditional.field]: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '0.9rem',
                  color: '#0f172a',
                  border: '2px solid #cbd5e1',
                  borderRadius: 8,
                  fontFamily: 'inherit',
                  transition: 'all 0.2s ease',
                  boxShadow: 'none',
                  outline: 'none',
                  background: 'white'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0077B5';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 119, 181, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#cbd5e1';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                }}
              />
            ) : (
              <CustomDropdown
                options={conditional.options}
                value={conditionalAnswer}
                onChange={(value) => setAnswers(prev => ({ ...prev, [question.id]: answer, [conditional.field]: value }))}
                placeholder="Select an option..."
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <section
      className="section questionnaire-fullscreen"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '90px 1rem 2rem 1rem',
        position: 'relative'
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        const tag = e.target.tagName?.toLowerCase();
        const isInputLike = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
        const isButton = tag === 'button';
        if (isInputLike || isButton) return;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Fixed Back Button - Below Progress Bar */}
      {currentQuestion === 0 && (
        <div style={{ 
          position: 'fixed', 
          top: '90px', 
          left: '2rem',
          zIndex: 10
        }}>
          <button
            type="button"
            onClick={openBackModal}
            style={{ 
              background: 'white', 
              border: '2px solid #e2e8f0', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8, 
              color: '#64748b', 
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#0077B5';
              e.currentTarget.style.color = '#0077B5';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 119, 181, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.color = '#64748b';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
            }}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      )}


      <div className="container" style={{ 
        maxWidth: '900px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
        padding: '0 1rem'
      }}>
        {/* Show heading only on first question */}
        {currentQuestion === 0 && (
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ 
              fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', 
              color: '#0f172a', 
              marginBottom: '12px', 
              fontWeight: 700,
              padding: '0 1rem'
            }}>
              Let's understand you better
            </h2>
            <p style={{ 
              color: '#64748b', 
              fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
              padding: '0 1rem'
            }}>
              Answer accurately to get the best results
            </p>
          </div>
        )}

        <div className="card questionnaire-card questionnaire-card-responsive" style={{ 
          maxWidth: '700px', 
          margin: '0 auto', 
          padding: 'clamp(20px, 4vw, 36px)', 
          border: '1px solid #e2e8f0', 
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          borderRadius: '16px',
          background: 'white',
          width: '100%'
        }}>
          {renderQuestion(questions[currentQuestion])}

          {/* Navigation Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: 28,
            paddingTop: 24,
            borderTop: '1px solid #e2e8f0'
          }}>
            <button 
              type="button" 
              onClick={handlePrevious} 
              disabled={currentQuestion === 0} 
              style={{ 
                visibility: currentQuestion === 0 ? 'hidden' : 'visible',
                padding: '11px 24px',
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#64748b',
                background: 'white',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#cbd5e1';
                e.target.style.color = '#475569';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.color = '#64748b';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Previous
            </button>
            <button 
              type="button" 
              onClick={handleNext} 
              disabled={!isCurrentQuestionAnswered() || isSubmitting}
              style={{ 
                padding: '11px 32px',
                fontSize: '0.95rem',
                fontWeight: 600,
                color: 'white',
                background: (!isCurrentQuestionAnswered() || isSubmitting) 
                  ? '#94a3b8' 
                  : 'linear-gradient(135deg, #0077B5 0%, #005885 100%)',
                border: 'none',
                borderRadius: 10,
                cursor: (!isCurrentQuestionAnswered() || isSubmitting) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: (!isCurrentQuestionAnswered() || isSubmitting) 
                  ? '0 1px 3px rgba(0, 0, 0, 0.1)' 
                  : '0 3px 12px rgba(0, 119, 181, 0.25)',
                opacity: (!isCurrentQuestionAnswered() || isSubmitting) ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!(!isCurrentQuestionAnswered() || isSubmitting)) {
                  e.target.style.boxShadow = '0 4px 16px rgba(0, 119, 181, 0.35)';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!(!isCurrentQuestionAnswered() || isSubmitting)) {
                  e.target.style.boxShadow = '0 3px 12px rgba(0, 119, 181, 0.25)';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              {isSubmitting ? 'Generating Analysis…' : (currentQuestion === questions.length - 1 ? 'Generate Analysis' : 'Next')}
            </button>
          </div>
        </div>
      </div>

      {/* Fixed Progress Bar - Top */}
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '70px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        boxShadow: '0 2px 15px rgba(0, 0, 0, 0.08)'
      }}>
        <div style={{ width: '100%', maxWidth: '900px', padding: '0 30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0077B5 0%, #00a0dc 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '700',
                fontSize: '0.9rem',
                boxShadow: '0 4px 10px rgba(0, 119, 181, 0.3)'
              }}>
                {currentQuestion + 1}
              </div>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>
                Question {currentQuestion + 1} of {questions.length}
              </span>
            </div>
            <div style={{
              padding: '6px 16px',
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: '20px',
              border: '1px solid #0077B5',
              color: '#0077B5',
              fontWeight: 700,
              fontSize: '0.85rem',
              boxShadow: '0 2px 8px rgba(0, 119, 181, 0.15)'
            }}>
              {Math.round(progress)}% Complete
            </div>
          </div>
          
          {/* Step Indicators */}
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'relative'
          }}>
            {Array.from({ length: questions.length }).map((_, index) => (
              <div
                key={index}
                style={{
                  flex: 1,
                  height: '6px',
                  backgroundColor: index <= currentQuestion ? '#0077B5' : '#e2e8f0',
                  borderRadius: '10px',
                  transition: 'all 0.4s ease',
                  boxShadow: index <= currentQuestion ? '0 2px 8px rgba(0, 119, 181, 0.3)' : 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {index <= currentQuestion && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
                    animation: 'shimmer 1.5s infinite'
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* Exit Confirmation Modal via Portal */}
      {showExitModal && createPortal(
        (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
            onClick={cancelBack}
          >
            <div
              className="card"
              style={{
                width: '100%',
                maxWidth: '520px',
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(2, 6, 23, 0.25)',
                padding: '1.5rem'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <ArrowLeft className="w-5 h-5" style={{ color: '#64748b' }} />
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1f2937' }}>Leave Questionnaire?</h3>
              </div>
              <p style={{ color: '#64748b', margin: 0, marginBottom: '1.25rem' }}>
                You’ll return to the start. This won’t clear saved results or answers in storage.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelBack}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmBack}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Responsive and Animation Styles */}
      <style>{`
        /* Smooth fade-in animation */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .questionnaire-card {
          animation: fadeInUp 0.4s ease;
        }

        /* Remove default section padding */
        .questionnaire-fullscreen.section {
          padding: 0 !important;
        }

        /* Smooth scrolling for checkbox lists */
        .question-container-responsive > div > div::-webkit-scrollbar {
          width: 5px;
        }

        .question-container-responsive > div > div::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .question-container-responsive > div > div::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }

        .question-container-responsive > div > div::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        @media (max-width: 768px) {
          .questionnaire-fullscreen {
            padding: 100px 0.5rem 1.5rem 0.5rem !important;
            min-height: 100vh !important;
            justify-content: flex-start !important;
          }

          .questionnaire-fullscreen > div:first-child {
            top: 100px !important;
            left: 1rem !important;
          }
          
          /* Mobile progress bar adjustments */
          .questionnaire-fullscreen > div[style*="position: fixed"][style*="top: 0"] {
            height: 90px !important;
            padding: 0 12px !important;
          }
          
          .questionnaire-fullscreen > div[style*="position: fixed"] > div {
            max-width: 100% !important;
            padding: 0 12px !important;
          }
          
          /* Progress bar text - smaller on mobile */
          .questionnaire-fullscreen > div[style*="position: fixed"] span {
            font-size: 0.75rem !important;
          }
          
          /* Current question badge - smaller */
          .questionnaire-fullscreen > div[style*="position: fixed"] > div > div:first-child > div:first-child > div:first-child {
            width: 26px !important;
            height: 26px !important;
            font-size: 0.75rem !important;
          }
          
          /* Progress percentage badge */
          .questionnaire-fullscreen > div[style*="position: fixed"] > div > div:first-child > div:last-child {
            padding: 4px 10px !important;
            font-size: 0.7rem !important;
          }
          
          /* Step indicators - thinner on mobile */
          .questionnaire-fullscreen > div[style*="position: fixed"] > div > div:last-child > div {
            height: 4px !important;
            gap: 3px !important;
          }
          
          /* Main card */
          .questionnaire-card-responsive {
            padding: 18px !important;
            border-radius: 12px !important;
            margin: 0 8px !important;
            max-width: 100% !important;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
          }

          .container {
            padding-left: 12px !important;
            padding-right: 12px !important;
            max-width: 100% !important;
          }

          /* Question container */
          .question-container-responsive {
            margin-top: 0 !important;
          }

          .question-header-responsive {
            gap: 10px !important;
            margin-bottom: 14px !important;
            flex-wrap: nowrap !important;
          }

          /* Question icon */
          .question-icon-responsive {
            width: 36px !important;
            height: 36px !important;
            border-radius: 8px !important;
            min-width: 36px !important;
          }
          
          .question-icon-responsive svg {
            width: 18px !important;
            height: 18px !important;
          }

          /* Question title */
          .question-title-responsive {
            font-size: 1rem !important;
            line-height: 1.35 !important;
            word-break: break-word !important;
          }

          /* Checkbox options */
          .question-container-responsive label {
            padding: 11px 12px !important;
            font-size: 0.875rem !important;
            gap: 8px !important;
            border-width: 1.5px !important;
          }
          
          .question-container-responsive label span {
            font-size: 0.875rem !important;
            line-height: 1.4 !important;
          }

          /* Checkbox grid container */
          .question-container-responsive > div > div[style*="display: grid"] {
            max-height: 50vh !important;
            gap: 8px !important;
          }

          /* Textarea */
          .question-container-responsive textarea {
            padding: 11px 12px !important;
            font-size: 0.9rem !important;
            min-height: 90px !important;
            max-height: 180px !important;
          }

          /* Conditional field */
          .question-container-responsive > div:last-child {
            margin-top: 14px !important;
            padding: 12px !important;
          }
          
          .question-container-responsive > div:last-child label {
            font-size: 0.8rem !important;
            margin-bottom: 6px !important;
          }

          .question-container-responsive input[type="text"] {
            padding: 9px 11px !important;
            font-size: 0.9rem !important;
          }

          /* Buttons container */
          .questionnaire-card-responsive > div:last-child {
            margin-top: 20px !important;
            padding-top: 18px !important;
            flex-wrap: nowrap !important;
          }

          /* Buttons */
          .questionnaire-card-responsive button {
            padding: 10px 18px !important;
            font-size: 0.875rem !important;
            border-radius: 8px !important;
            white-space: nowrap !important;
          }

          /* Back button */
          .questionnaire-fullscreen > div:first-child button {
            padding: 8px 12px !important;
            font-size: 0.85rem !important;
            gap: 6px !important;
          }

          .questionnaire-fullscreen > div:first-child button svg {
            width: 14px !important;
            height: 14px !important;
          }

          /* Hero heading */
          .questionnaire-fullscreen > div:nth-child(2) > div:first-child h2 {
            font-size: 1.4rem !important;
            margin-bottom: 8px !important;
            padding: 0 0.5rem !important;
          }

          .questionnaire-fullscreen > div:nth-child(2) > div:first-child p {
            font-size: 0.9rem !important;
            padding: 0 0.5rem !important;
          }
          
          .questionnaire-fullscreen > div:nth-child(2) > div:first-child {
            margin-bottom: 24px !important;
          }

          /* Exit modal */
          .card {
            max-width: calc(100% - 32px) !important;
            padding: 1.25rem !important;
          }

          .card h3 {
            font-size: 1.05rem !important;
          }

          .card p {
            font-size: 0.875rem !important;
            margin-bottom: 1rem !important;
          }
          
          .card button {
            padding: 9px 16px !important;
            font-size: 0.875rem !important;
          }
        }

        @media (max-width: 480px) {
          /* Very small screens */
          .questionnaire-fullscreen {
            padding: 95px 0.5rem 1.5rem 0.5rem !important;
          }
          
          .questionnaire-card-responsive {
            padding: 14px !important;
            margin: 0 6px !important;
          }

          .container {
            max-width: 100% !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }

          /* Progress bar adjustments for very small screens */
          .questionnaire-fullscreen > div[style*="position: fixed"][style*="top: 0"] {
            height: 85px !important;
            padding: 0 8px !important;
          }
          
          .questionnaire-fullscreen > div[style*="position: fixed"] > div {
            padding: 0 8px !important;
          }
          
          .questionnaire-fullscreen > div[style*="position: fixed"] span {
            font-size: 0.7rem !important;
          }
          
          .questionnaire-fullscreen > div[style*="position: fixed"] > div > div:first-child > div:first-child > div:first-child {
            width: 24px !important;
            height: 24px !important;
            font-size: 0.7rem !important;
          }
          
          .questionnaire-fullscreen > div[style*="position: fixed"] > div > div:first-child > div:last-child {
            padding: 3px 8px !important;
            font-size: 0.65rem !important;
          }
          
          .questionnaire-fullscreen > div[style*="position: fixed"] > div > div:first-child {
            gap: 8px !important;
          }

          /* Question header */
          .question-header-responsive {
            gap: 8px !important;
            margin-bottom: 12px !important;
          }

          .question-icon-responsive {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
          }

          .question-icon-responsive svg {
            width: 16px !important;
            height: 16px !important;
          }

          .question-title-responsive {
            font-size: 0.95rem !important;
            line-height: 1.35 !important;
          }

          /* Checkbox options */
          .question-container-responsive label {
            padding: 10px 11px !important;
            font-size: 0.85rem !important;
            gap: 7px !important;
          }
          
          .question-container-responsive label span {
            font-size: 0.85rem !important;
          }

          .question-container-responsive input[type="checkbox"] {
            width: 15px !important;
            height: 15px !important;
            min-width: 15px !important;
          }
          
          /* Checkbox grid container */
          .question-container-responsive > div > div[style*="display: grid"] {
            max-height: 45vh !important;
            gap: 7px !important;
          }

          /* Textarea */
          .question-container-responsive textarea {
            padding: 10px 11px !important;
            font-size: 0.875rem !important;
            min-height: 80px !important;
            max-height: 160px !important;
          }

          /* Conditional field */
          .question-container-responsive > div:last-child {
            margin-top: 12px !important;
            padding: 10px !important;
          }
          
          .question-container-responsive > div:last-child label {
            font-size: 0.75rem !important;
          }

          .question-container-responsive input[type="text"] {
            padding: 8px 10px !important;
            font-size: 0.875rem !important;
          }

          /* Buttons container */
          .questionnaire-card-responsive > div:last-child {
            margin-top: 18px !important;
            padding-top: 16px !important;
          }

          /* Buttons */
          .questionnaire-card-responsive button {
            padding: 9px 14px !important;
            font-size: 0.8rem !important;
          }

          /* Back button */
          .questionnaire-fullscreen > div:first-child {
            top: 95px !important;
            left: 0.75rem !important;
          }
          
          .questionnaire-fullscreen > div:first-child button {
            padding: 7px 10px !important;
            font-size: 0.8rem !important;
            gap: 5px !important;
          }

          .questionnaire-fullscreen > div:first-child button svg {
            width: 13px !important;
            height: 13px !important;
          }

          /* Hero heading */
          .questionnaire-fullscreen > div:nth-child(2) > div:first-child h2 {
            font-size: 1.25rem !important;
            margin-bottom: 6px !important;
            padding: 0 0.25rem !important;
          }

          .questionnaire-fullscreen > div:nth-child(2) > div:first-child p {
            font-size: 0.85rem !important;
            padding: 0 0.25rem !important;
          }
          
          .questionnaire-fullscreen > div:nth-child(2) > div:first-child {
            margin-bottom: 20px !important;
          }

          /* Modal */
          .card {
            max-width: calc(100% - 24px) !important;
            padding: 1rem !important;
          }

          .card h3 {
            font-size: 0.95rem !important;
          }
          
          .card p {
            font-size: 0.825rem !important;
          }

          .card button {
            padding: 8px 14px !important;
            font-size: 0.8rem !important;
          }
          
          .card > div:last-child {
            gap: 0.5rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Questionnaire;
